/* The one pricing function. The browser uses it for the live estimate; the
   server runs it again on the layout it has validated when the customer
   orders, and only the server's result is ever sent on. Defaults reproduce
   printees.co.nz exactly (rates rounded to the cent, then × length, rounded
   to the cent; the bulk tier comes from the TOTAL metres in the order). */

import type { PricingConfig, Transfer, Tier, ByLength, TransferPricing } from './config.js';
import { RATE_UNIT_MM } from './config.js';

export interface SheetForPricing {
  kind: 'fixed' | 'length' | 'roll';
  fixedKey?: string;
  widthMm: number;
  /** the sheet's length on the canvas (by-length sheets are billed rounded up) */
  lengthMm: number;
}

export interface PriceOptions {
  group?: string | null;
  code?: string | null;
  rush?: boolean;
  now?: Date;
}

export interface PriceLine {
  index: number;
  kind: SheetForPricing['kind'];
  name: string;
  widthMm: number;
  billedLengthMm: number;
  /** by-length only: $/m after group + tier */
  ratePerMetre?: number;
  price: number;
  /** price at the full rate (before group / tier) */
  fullPrice: number;
}

export interface PriceResult {
  currency: string;
  lines: PriceLine[];
  /** billed metres of by-length film (decides the tier) */
  lengthMetres: number;
  /** all film, fixed sheets included (decides whether Rush is offered) */
  filmMetres: number;
  baseRatePerMetre: number;
  tier: { fromMetres: number; percentOff: number } | null;
  nextTier: { fromMetres: number; percentOff: number; extraMetres: number; ratePerMetre: number } | null;
  subtotal: number;
  discount: { code: string; amount: number } | null;
  codeError: string | null;
  rush: { available: boolean; applied: boolean; amount: number; percent: number; reason?: string };
  total: number;
  tax: number;
  exTax: number;
  taxLabel: string;
  saving: number;
  /** one by-length sheet: a longer sheet that costs less (next tier) */
  better: { lengthMm: number; total: number; saving: number } | null;
  errors: string[];
}

const r2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

export function ratePerMetre(byLength: Pick<ByLength, 'rate' | 'rateUnit'>): number {
  return byLength.rate * (1000 / RATE_UNIT_MM[byLength.rateUnit]);
}

/** By-length billing: to the nearest whole mm, then at least the minimum, rounded UP to the
    billing step — the same as the website's calculator (1.45 m → 1.5 m, 2000.3 mm → 2.0 m). */
export function billedLengthMm(lengthMm: number, bl: Pick<ByLength, 'minLengthMm' | 'billingStepMm'>): number {
  let L = Math.round(Math.max(lengthMm, 0));
  if (bl.billingStepMm > 0) L = Math.ceil(L / bl.billingStepMm - 1e-9) * bl.billingStepMm;
  L = Math.max(L, bl.minLengthMm);
  return Math.round(L * 1000) / 1000;
}

/** A roll's length: billed as above, but never shorter than the artwork on it (`contentMm`). */
export function rollLengthMm(needMm: number, contentMm: number, bl: Pick<ByLength, 'minLengthMm' | 'billingStepMm'>): number {
  let L = billedLengthMm(needMm, bl);
  const step = bl.billingStepMm > 0 ? bl.billingStepMm : 1;
  while (L < contentMm - 1e-6) L = Math.round((L + step) * 1000) / 1000;
  return L;
}

/** The tier that applies to a total of `metres`, and the one after it. */
export function tierFor(tiers: Tier[], metres: number): { cur: Tier | null; next: Tier | null } {
  const sorted = [...tiers].sort((a, b) => a.fromMetres - b.fromMetres);
  let cur: Tier | null = null, next: Tier | null = null;
  for (const t of sorted) {
    const hit = t.exclusive ? metres > t.fromMetres + 1e-9 : metres >= t.fromMetres - 1e-9;
    if (hit) cur = t; else { next = t; break; }
  }
  return { cur, next };
}

function codeFor(cfg: PricingConfig, code: string | null | undefined, now: Date) {
  if (!code) return { found: null as PricingConfig['discountCodes'][number] | null, error: null as string | null };
  const c = cfg.discountCodes.find(d => d.code.toLowerCase() === code.trim().toLowerCase());
  if (!c || !c.active) return { found: null, error: 'That code isn’t valid.' };
  if (c.expiresAt && new Date(c.expiresAt).getTime() < now.getTime()) return { found: null, error: 'That code has expired.' };
  return { found: c, error: null };
}

export function priceSheets(
  sheets: SheetForPricing[],
  transfer: Transfer,
  cfg: PricingConfig,
  opts: PriceOptions = {}
): PriceResult {
  const tp: TransferPricing = cfg.transfers[transfer];
  const errors: string[] = [];
  const group = opts.group ? cfg.groups[opts.group] : undefined;
  const bl = tp.byLength;

  let base = ratePerMetre(bl);
  if (group?.rates && group.rates[transfer] !== undefined) base = ratePerMetre({ rate: group.rates[transfer] as number, rateUnit: bl.rateUnit });
  if (group?.percentOff) base = r2(base * (1 - group.percentOff));
  const useTiers = !(group && group.tiers === false);

  // billed lengths first: the tier depends on the total
  const billed = sheets.map(s => s.kind === 'fixed' ? s.lengthMm : billedLengthMm(s.lengthMm, bl));
  let lengthMetres = 0, filmMetres = 0;
  sheets.forEach((s, i) => {
    filmMetres += billed[i] / 1000;
    if (s.kind !== 'fixed') lengthMetres += billed[i] / 1000;
  });
  lengthMetres = Math.round(lengthMetres * 1000) / 1000;
  filmMetres = Math.round(filmMetres * 1000) / 1000;

  const t = useTiers ? tierFor(tp.tiers, lengthMetres) : { cur: null, next: null };
  const off = t.cur ? t.cur.percentOff : 0;
  const rate = r2(base * (1 - off));
  const fullRate = ratePerMetre(bl);

  const lines: PriceLine[] = sheets.map((s, i) => {
    if (s.kind === 'fixed') {
      const f = tp.fixedSheets.find(x => x.key === s.fixedKey);
      if (!f) {
        errors.push('Sheet ' + (i + 1) + ': unknown sheet size.');
        return { index: i, kind: s.kind, name: 'Sheet', widthMm: s.widthMm, billedLengthMm: s.lengthMm, price: 0, fullPrice: 0 };
      }
      const price = group?.percentOff ? r2(f.price * (1 - group.percentOff)) : f.price;
      return { index: i, kind: s.kind, name: f.name, widthMm: f.widthMm, billedLengthMm: f.lengthMm, price, fullPrice: f.price };
    }
    if (!bl.enabled) errors.push('Sheets by the metre aren’t available right now.');
    const L = billed[i];
    if (L > bl.maxLengthMm + 1e-6) errors.push('Sheet ' + (i + 1) + ' is longer than ' + bl.maxLengthMm / 1000 + ' m.');
    const metres = L / 1000;
    return {
      index: i, kind: s.kind, name: trimM(metres) + ' sheet', widthMm: bl.widthMm, billedLengthMm: L,
      ratePerMetre: rate, price: r2(rate * metres), fullPrice: r2(fullRate * metres)
    };
  });

  let subtotal = r2(lines.reduce((a, l) => a + l.price, 0));
  const full = r2(lines.reduce((a, l) => a + l.fullPrice, 0));

  // discount code
  const now = opts.now || new Date();
  const code = codeFor(cfg, opts.code, now);
  let discount: PriceResult['discount'] = null;
  let codeError = code.error;
  if (code.found) {
    const c = code.found;
    if (c.minSubtotal && subtotal < c.minSubtotal) codeError = 'That code needs an order of at least $' + c.minSubtotal.toFixed(2) + '.';
    else {
      let amt = 0;
      if (c.percentOff) amt += r2(subtotal * c.percentOff);
      if (c.amountOff) amt += c.amountOff;
      amt = Math.min(amt, subtotal);
      discount = { code: c.code, amount: r2(amt) };
    }
  }
  const afterCode = r2(subtotal - (discount ? discount.amount : 0));

  // rush
  const rushAvail = cfg.rush.enabled && filmMetres <= cfg.rush.maxMetres + 1e-9;
  const rushApplied = !!opts.rush && rushAvail;
  const rushAmount = rushApplied ? r2(afterCode * cfg.rush.percent) : 0;
  const total = r2(afterCode + rushAmount);
  const tax = cfg.taxInclusive ? r2(total - total / (1 + cfg.taxRate)) : r2(total * cfg.taxRate);
  const exTax = cfg.taxInclusive ? r2(total - tax) : total;

  // next tier + "longer is cheaper"
  let nextTier: PriceResult['nextTier'] = null;
  let better: PriceResult['better'] = null;
  if (t.next && lengthMetres > 0) {
    const nrate = r2(base * (1 - t.next.percentOff));
    const needM = t.next.exclusive ? t.next.fromMetres + (bl.billingStepMm || 1) / 1000 : t.next.fromMetres;
    nextTier = { fromMetres: t.next.fromMetres, percentOff: t.next.percentOff, extraMetres: Math.max(0, Math.round((needM - lengthMetres) * 1000) / 1000), ratePerMetre: nrate };
    const lenIdx = sheets.map((s, i) => (s.kind !== 'fixed' ? i : -1)).filter(i => i >= 0);
    if (lenIdx.length === 1 && !opts.code) {
      const i = lenIdx[0];
      const L2 = billedLengthMm(billed[i] + nextTier.extraMetres * 1000, bl);
      if (L2 <= bl.maxLengthMm + 1e-6 && L2 > billed[i]) {
        const trial = sheets.map((s, j) => (j === i ? { ...s, lengthMm: L2 } : s));
        const alt = priceSheets(trial, transfer, cfg, { ...opts, code: null });
        if (alt.total < total - 0.004) better = { lengthMm: L2, total: alt.total, saving: r2(total - alt.total) };
      }
    }
  }

  return {
    currency: cfg.currency,
    lines,
    lengthMetres,
    filmMetres,
    baseRatePerMetre: r2(base),
    tier: t.cur ? { fromMetres: t.cur.fromMetres, percentOff: t.cur.percentOff } : null,
    nextTier,
    subtotal,
    discount,
    codeError,
    rush: { available: rushAvail, applied: rushApplied, amount: rushAmount, percent: cfg.rush.percent, reason: rushAvail ? undefined : cfg.rush.enabled ? 'Rush is available on orders up to ' + cfg.rush.maxMetres + ' m.' : 'Rush isn’t offered right now.' },
    total,
    tax,
    exTax,
    taxLabel: cfg.taxLabel,
    saving: r2(full - subtotal + (discount ? discount.amount : 0)),
    better,
    errors
  };
}

function trimM(m: number): string {
  return String(Math.round(m * 100) / 100) + ' m';
}
